import { Component, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { RouterModule, Router, NavigationEnd } from "@angular/router"
import { SidebarModule } from "primeng/sidebar"
import { ButtonModule } from "primeng/button"
import { SkeletonModule } from 'primeng/skeleton'
import { DrawerModule } from 'primeng/drawer';
import { ModuleService } from 'src/app/services/authentication/module.service';
import { ModuleModel } from 'src/app/models/authentication/module.model';
import { WindowModel } from 'src/app/models/authentication/window.model';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { TooltipModule } from 'primeng/tooltip';
import { BreadcrumbService } from "src/app/services/breadcrumb/breadcrumb.service"
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-new-menu',
  imports: [TooltipModule, DrawerModule, CommonModule, RouterModule, SidebarModule, ButtonModule, SkeletonModule, FormsModule],
  templateUrl: './new-menu.component.html',
  styleUrls: ['./new-menu.component.scss']
})

export class NewMenuComponent implements OnInit {
  modules: Array<ModuleModel> = new Array<ModuleModel>();
  expandedModules: Set<number> = new Set<number>();
  isLoading: boolean = true;
  currentUrl: string = '';
  activeModuleId: number | null = null;
  activeWindowId: number | null = null;
  searchTerm: string = '';
  drawerWidth = "280px"
  setSidebarOpen = false
  isExpanded: boolean = false;

  constructor(
    private moduleService: ModuleService,
    private router: Router,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit() {
    this.getOptionsMenu();
    this.subscribeToRouteChanges();
    this.updateActiveStates();
  }

  ngOnChanges() {
    this.updateActiveStates();
    this.breadcrumbService.setModules(this.modules)
  }

  async getOptionsMenu() {
    try {
      this.isLoading = true;

      this.modules = await firstValueFrom(this.moduleService.getMenuByUser());
      this.breadcrumbService.setModules(this.modules);

      // Ordenar módulos por consecutive
      this.modules.sort((a, b) => a.consecutive - b.consecutive);

      // Ordenar ventanas dentro de cada módulo
      this.modules.forEach(module => {
        if (module.windows && module.windows.length > 0) {
          module.windows.sort((a, b) => a.consecutive - b.consecutive);
        }
      });

      // Determinar el módulo y ventana activos basados en la URL actual
      this.updateActiveStates();

      localStorage.setItem(AuthenticationContants.keyMenuUserLocalStorage, JSON.stringify(this.modules));
    } catch (error) {
      console.error('Error al buscar el menu por usuario.', error);
    } finally {
      this.isLoading = false;
    }
  }

  private findWindowByUrl(urlSegment: string): { module: ModuleModel, window: WindowModel } | null {
    for (const module of this.modules) {
      if (module.windows?.length) {
        for (const window of module.windows) {
          const windowSegment = window.urlWindow.split('/')[0];
          if (windowSegment === urlSegment) {
            return { module, window };
          }
        }
      }
    }
    return null;
  }

  private resetActiveStates() {
    this.activeWindowId = null;
    this.activeModuleId = null;
    this.expandedModules.clear();
  }

  // Suscribirse a cambios de ruta para mantener el estado activo
  subscribeToRouteChanges() {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.url;
        this.updateActiveStates();
      });
  }

  // Actualizar estados activos basados en la URL actual
  updateActiveStates() {
    // Resetear estados activos
    this.resetActiveStates();

    const currentUrlSegment = (this.currentUrl || this.router.url).split('/')[1];

    if (!currentUrlSegment) return;

    // Buscar el módulo y ventana activos basados en la URL actual
    const matchingWindow = this.findWindowByUrl(currentUrlSegment);

    if (matchingWindow) {
      this.activeModuleId = matchingWindow.module.moduleId;
      this.activeWindowId = matchingWindow.window.windowId;
      this.expandedModules.add(matchingWindow.module.moduleId);
    }
  }


  // Verificar si un módulo está expandido
  isModuleExpanded(moduleId: number): boolean {
    return this.expandedModules.has(moduleId);
  }

  // Verificar si un módulo está activo
  isModuleActive(module: ModuleModel): boolean {
    return this.activeModuleId === module.moduleId;
  }

  // Verificar si una ventana está activa
  isWindowActive(window: WindowModel): boolean {
    return this.activeWindowId === window.windowId;
  }

  openAsLink(event: MouseEvent, module: any) {
    event.preventDefault(); // evitar menú contextual
    window.open(`/ruta/${module.moduleId}`, '_blank'); // abre en nueva pestaña
  }

  // Toggle de expansión de módulo
  toggleModule(module: ModuleModel) {
    // Expandir sidebar si está colapsado
    if (!this.isExpanded) {
      this.expandedModules.clear();
      this.isExpanded = true;
    }

    // Solo alternar si el módulo tiene ventanas
    if (!module.windows?.length) return;

    // Alternar estado de expansión del módulo
    if (this.expandedModules.has(module.moduleId)) {
      this.expandedModules.delete(module.moduleId);
    } else {
      this.expandedModules.add(module.moduleId);
    }
  }

  // Obtener ventanas ordenadas
  getSortedWindows(windows: WindowModel[]): WindowModel[] {
    return windows.sort((a, b) => a.consecutive - b.consecutive);
  }

  // Método para alternar la barra lateral
  toggleSidebar() {
    this.expandedModules.clear();
    this.isExpanded = !this.isExpanded;
  }

  // Método para manejar la navegación
  navigateToWindow(window: WindowModel | string) {
    this.resetActiveStates();

    const url = typeof window === 'string' ? window : window.urlWindow;
    this.router.navigate([`/${url}`]);
    this.isExpanded = false;
  }

  navigateToModule(module: ModuleModel) {
    this.resetActiveStates();

    if (module.urlModule) {
      this.router.navigate([`/${module.urlModule}`]);
    }
  }

  onWindowLinkClick(event: MouseEvent) {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }

    this.searchTerm = '';
    this.resetActiveStates();
    this.isExpanded = false;
  }

  getFilteredWindows(module: ModuleModel): WindowModel[] {
    const windows = this.getSortedWindows(module.windows || []);
    if (!this.searchTerm?.trim()) return windows;
    const term = this.searchTerm.trim().toLowerCase();
    return windows.filter(w => w.labelName?.toLowerCase().includes(term));
  }

  hasMatches(module: ModuleModel): boolean {
    if (!module.windows?.length) return false;
    return this.getFilteredWindows(module).length > 0;
  }
}